import React from 'react';
import { BottomNavigation, BottomNavigationAction, useMediaQuery, useTheme } from '@mui/material';
import { Home, Login, Language as LanguageIcon, List, CalendarToday } from '@mui/icons-material';
import { useLanguage } from '../hooks/useLanguage';
import LanguageDialog from './LanguageDialog';

interface PublicBottomNavProps {
  currentView: string;
  onViewChange: (view: string) => void;
  onLogin: () => void;
}

const PublicBottomNav: React.FC<PublicBottomNavProps> = ({ currentView, onViewChange, onLogin }) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showLang, setShowLang] = React.useState(false);

  // A/B Test Flag: Read from global feature flags set by PublicHomePage
  const [showCalendarAndActivitiesMenu, setShowCalendarAndActivitiesMenu] = React.useState(false);

  React.useEffect(() => {
    const readFlag = () => {
      const globalWindow = window as typeof window & {
        __PUBLIC_FEATURE_FLAGS?: Record<string, unknown>;
      };
      const flagValue = !!globalWindow.__PUBLIC_FEATURE_FLAGS?.showCalendarAndActivitiesMenu;
      setShowCalendarAndActivitiesMenu(flagValue);
    };

    readFlag();
    const evt = 'treffpunkts:publicFeatureFlags';
    window.addEventListener(evt, readFlag);
    return () => window.removeEventListener(evt, readFlag);
  }, []);

  const SHOW_CALENDAR_AND_ACTIVITIES_MENU = showCalendarAndActivitiesMenu;

  if (!isMobile) return null;

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (newValue === 'login') {
      onLogin();
    } else if (newValue === 'language') {
      setShowLang(true);
    } else {
      onViewChange(newValue);
    }
  };

  const colors = {
    primary: '#6A4C93',
    secondary: '#1982C4',
    warning: '#FFCA3A',
  };

  const langText = t('navigation.language');
  const languageLabel = langText === 'navigation.language' ? (navigator.language?.startsWith('de') ? 'Sprache' : 'Language') : langText;

  return (
    <BottomNavigation
      value={currentView}
      onChange={handleChange}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
        borderTop: `2px solid ${colors.secondary}`,
        // Ensure it stays fixed even when scrolling
        transform: 'translateZ(0)',
        willChange: 'transform',
        '& .MuiBottomNavigationAction-root': {
          color: 'rgba(255,255,255,0.85)',
          '&.Mui-selected': {
            color: colors.warning,
          },
          '& .MuiSvgIcon-root': {
            color: 'inherit',
          },
          '& .MuiBottomNavigationAction-label': {
            fontWeight: 600,
          },
        },
      }}
      showLabels
    >
      <BottomNavigationAction label={t('navigation.home')} value="public-home" icon={<Home />} />
      {/* A/B TEST: Activities and Calendar menu items - Hidden for A/B testing */}
      {SHOW_CALENDAR_AND_ACTIVITIES_MENU && (
        <>
          <BottomNavigationAction label={t('navigation.activities')} value="public-activities" icon={<List />} />
          <BottomNavigationAction label={t('navigation.calendar')} value="calendar" icon={<CalendarToday />} />
        </>
      )}
      <BottomNavigationAction label={t('navigation.login')} value="login" icon={<Login />} />
      <BottomNavigationAction label={languageLabel} value="language" icon={<LanguageIcon />} />
      <LanguageDialog open={showLang} onClose={() => setShowLang(false)} />
    </BottomNavigation>
  );
};

export default PublicBottomNav;


